import React, { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Shared lightbox with ← → keyboard + button navigation.
 *
 * Two call shapes:
 *   • Single:  items=[{ url, alt?, type?, label? }], index=0
 *   • Gallery: items=[{...}, {...}, ...], index=N
 *
 * `type` defaults to 'image'; set 'video' for video URLs.
 * Arrow controls only render when items.length > 1.
 */
export default function NavigableLightbox({ items, index, onIndexChange, onClose, testid = 'nav-lightbox' }) {
  const total = items?.length || 0;
  const cur = items?.[index];

  const goPrev = useCallback(() => {
    if (total <= 1) return;
    onIndexChange((index - 1 + total) % total);
  }, [index, total, onIndexChange]);

  const goNext = useCallback(() => {
    if (total <= 1) return;
    onIndexChange((index + 1) % total);
  }, [index, total, onIndexChange]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
      else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrev, goNext, onClose]);

  if (!cur) return null;

  const type = cur.type || 'image';
  const label = cur.label || cur.alt || '';

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-6"
      style={{ background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
      data-testid={testid}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-lg text-white hover:bg-white/10 z-10"
        data-testid={`${testid}-close`}
      >
        <X className="w-5 h-5" />
      </button>

      {total > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full text-white hover:bg-white/10 transition z-10"
            data-testid={`${testid}-prev`}
            title="Previous (←)"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full text-white hover:bg-white/10 transition z-10"
            data-testid={`${testid}-next`}
            title="Next (→)"
          >
            <ChevronRight className="w-7 h-7" />
          </button>
        </>
      )}

      <div onClick={(e) => e.stopPropagation()} className="max-w-[90vw] max-h-[90vh]">
        {type === 'video' ? (
          <video
            key={cur.url}
            src={cur.url}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
            controls
            autoPlay
            loop
            data-testid={`${testid}-video`}
          />
        ) : (
          <img
            key={cur.url}
            src={cur.url}
            alt={label || 'Expanded media'}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
            data-testid={`${testid}-image`}
          />
        )}
        {(label || total > 1) && (
          <div className="mt-3 flex items-center justify-center gap-3 text-xs font-mono uppercase tracking-widest text-white/60">
            {label && <span>{label}</span>}
            {total > 1 && (
              <span className="px-2 py-0.5 rounded-full bg-white/10 text-white/80" data-testid={`${testid}-counter`}>
                {index + 1} / {total}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
